import { displayTag, type Member } from '@engineer-os/chat';
import { metaFor } from '../../lib/chat';
import { useStrings } from '../../state/useLocale';
import { Machine } from '../../ui';
import { Identity } from './Identity';

/**
 * Who is in the room, one line each, the server's order.
 *
 * Your own line is bolder, not coloured: the same rule as your own messages.
 * There is no presence dot, because everyone in this list is present by
 * definition — it is the list of open sockets — and a dot that is always on
 * says nothing.
 *
 * Every row opens the card that says who somebody is (ProfileCard.tsx), and
 * the whole row is the button rather than the name inside it. This is the
 * list whose entire purpose is being read down and picked from — a 280px
 * panel gives a name about twelve characters, and a target that is the name
 * only would be twelve characters wide. The tag and the meta are part of the
 * same answer, so they are part of the same button.
 */
export function MemberList({ members, you, onOpen }: { members: Member[]; you: Member | null; onOpen(m: Member): void }) {
  const s = useStrings().chat;
  if (members.length === 0) {
    return <p className="chat-empty">{s.members.empty}</p>;
  }
  return (
    <ul className="chat-members" aria-label={s.members.label}>
      {members.map((m) => {
        const own = you !== null && m.tag === you.tag;
        const meta = metaFor(m.level, m.rank);
        return (
          <li key={m.tag} className={own ? 'chat-member is-you' : 'chat-member'}>
            {/* The row is the button, so `Identity` is handed no `onOpen` of
                its own: a button inside a button is not markup a browser will
                give either of them back. */}
            <button type="button" className="chat-member-btn" title={s.who.open} onClick={() => onOpen(m)}>
              <Identity m={m} />
              {/* The tag label is four hex characters the server derived, and the
                  meta is `L07 · Junior Engineer`. Both were written by the
                  machine and stay the way it wrote them (docs/i18n.md §7). */}
              <Machine className="msg-tag">#{displayTag(m.tag)}</Machine>
              {meta && <Machine className="msg-meta">{meta}</Machine>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default MemberList;
